import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Avatar,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupIcon from "@mui/icons-material/Group";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { toast } from "react-toastify";
import { createAdminLiveEvent, updateAdminLiveEvent } from "../../../api/dev/liveEventsAdminApi";

const CATEGORY_OPTIONS = [
  "Mindfulness",
  "Stress Management",
  "Work-Life Balance",
  "Mental Health",
  "Sleep & Recovery",
  "Focus & Productivity",
  "Resilience",
  "General",
];

const RECUR_OPTIONS = ["None", "Daily", "Weekly", "Bi-weekly", "Monthly"];
const STATUS_OPTIONS = ["Active", "Draft", "Cancelled"];

// Helper to convert date to datetime-local format (YYYY-MM-DDTHH:MM)
const toDateTimeLocalString = (dateVal) => {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const LiveEventFormDialog = ({
  open,
  onClose,
  onSuccess,
  event = null, // if null -> Create mode, if populated -> Edit mode
}) => {
  const isEdit = Boolean(event && event.id);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "General",
    presenter: "MindSherpa Presenter",
    presenter_email: "",
    presenter_bio: "",
    presenter_avatar: "",
    cover_image: "",
    event_date: "",
    duration_minutes: 30,
    capacity: 100,
    stream_link: "",
    recur_interval: "None",
    status: "Active",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event && open) {
      setFormData({
        title: event.title || "",
        description: event.description || "",
        category: event.category || "General",
        presenter: event.presenter || "MindSherpa Presenter",
        presenter_email: event.presenter_email || "",
        presenter_bio: event.presenter_bio || "",
        presenter_avatar: event.presenter_avatar || "",
        cover_image: event.cover_image || "",
        event_date: toDateTimeLocalString(event.event_date),
        duration_minutes: event.duration_minutes || 30,
        capacity: event.capacity || 100,
        stream_link: event.stream_link || "",
        recur_interval: event.recur_interval || "None",
        status: event.status || "Active",
      });
      setErrors({});
    } else if (open) {
      // Set default start date to next day 10:00 AM
      const nextDay = new Date();
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(10, 0, 0, 0);

      setFormData({
        title: "",
        description: "",
        category: "General",
        presenter: "MindSherpa Presenter",
        presenter_email: "",
        presenter_bio: "",
        presenter_avatar: "",
        cover_image: "",
        event_date: toDateTimeLocalString(nextDay),
        duration_minutes: 30,
        capacity: 100,
        stream_link: "",
        recur_interval: "None",
        status: "Active",
      });
      setErrors({});
    }
  }, [event, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Event title is required";
    }
    if (!formData.presenter.trim()) {
      newErrors.presenter = "Presenter name is required";
    }
    if (!formData.presenter_email || !formData.presenter_email.trim()) {
      newErrors.presenter_email = "Presenter email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.presenter_email.trim())) {
        newErrors.presenter_email = "Please enter a valid presenter email address";
      }
    }
    if (!formData.event_date) {
      newErrors.event_date = "Event date & time is required";
    }
    if (Number(formData.duration_minutes) <= 0) {
      newErrors.duration_minutes = "Duration must be greater than 0";
    }
    if (Number(formData.capacity) <= 0) {
      newErrors.capacity = "Capacity must be greater than 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, notifyAttendees = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);

      // Convert local date string to ISO
      const isoDate = new Date(formData.event_date).toISOString();
      const payload = {
        ...formData,
        event_date: isoDate,
        duration_minutes: Number(formData.duration_minutes),
        capacity: Number(formData.capacity),
      };

      if (isEdit) {
        await updateAdminLiveEvent(event.id || event.event_id, payload, notifyAttendees);
        if (notifyAttendees) {
          toast.success("Live event updated & notification email sent to registered attendees!");
        } else {
          toast.success("Live event updated successfully!");
        }
      } else {
        await createAdminLiveEvent(payload);
        toast.success("New live event created successfully!");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to save live event. Please check inputs and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E2E8F0",
          py: 2,
          px: 3,
          bgcolor: "#F8FAFC",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#154C3A">
            {isEdit ? "Edit Live Event" : "Create New Live Event"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isEdit
              ? `Update session details for #${event?.id || event?.event_id}`
              : "Fill in the required information to schedule a new live guidance session"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, overflowY: "auto", maxHeight: "calc(82vh - 120px)" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* 1. Event Title */}
            <TextField
              label="Event Title"
              placeholder="e.g. Mindful Living & Stress Resilience"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange("title")}
              error={Boolean(errors.title)}
              helperText={errors.title}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 2. Description */}
            <TextField
              label="Description"
              placeholder="Describe what attendees will learn and practice in this live session..."
              multiline
              rows={3}
              fullWidth
              value={formData.description}
              onChange={handleChange("description")}
            />

            {/* 3. Category */}
            <TextField
              select
              label="Category"
              fullWidth
              value={formData.category}
              onChange={handleChange("category")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>

            {/* 4. Event Status */}
            <TextField
              select
              label="Event Status"
              fullWidth
              value={formData.status}
              onChange={handleChange("status")}
            >
              {STATUS_OPTIONS.map((st) => (
                <MenuItem key={st} value={st}>
                  {st}
                </MenuItem>
              ))}
            </TextField>

            {/* 5. Presenter Name */}
            <TextField
              label="Presenter Name"
              placeholder="e.g. Dr. Maya Sherpa"
              fullWidth
              required
              value={formData.presenter}
              onChange={handleChange("presenter")}
              error={Boolean(errors.presenter)}
              helperText={errors.presenter}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 5b. Presenter Email */}
            <TextField
              label="Presenter Email"
              placeholder="e.g. presenter@mindsherpa.health"
              fullWidth
              required
              value={formData.presenter_email}
              onChange={handleChange("presenter_email")}
              error={Boolean(errors.presenter_email)}
              helperText={
                errors.presenter_email ||
                "Attendee roster list will be automatically sent to this email 30 minutes before the session"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 6. Presenter Bio */}
            <TextField
              label="Presenter Bio"
              placeholder="Brief background about the presenter's credentials and expertise..."
              multiline
              rows={2}
              fullWidth
              value={formData.presenter_bio}
              onChange={handleChange("presenter_bio")}
            />

            {/* 7. Presenter Avatar URL */}
            <TextField
              label="Presenter Avatar URL"
              placeholder="https://images.unsplash.com/..."
              fullWidth
              value={formData.presenter_avatar}
              onChange={handleChange("presenter_avatar")}
              helperText="Optional avatar photo URL for the presenter"
              InputProps={{
                endAdornment: formData.presenter_avatar ? (
                  <InputAdornment position="end">
                    <Avatar
                      src={formData.presenter_avatar}
                      sx={{ width: 28, height: 28 }}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* 8. Event Date & Time */}
            <TextField
              label="Event Date & Time"
              type="datetime-local"
              fullWidth
              required
              value={formData.event_date}
              onChange={handleChange("event_date")}
              error={Boolean(errors.event_date)}
              helperText={errors.event_date || "Local session start date and time"}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 9. Duration */}
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              required
              value={formData.duration_minutes}
              onChange={handleChange("duration_minutes")}
              error={Boolean(errors.duration_minutes)}
              helperText={errors.duration_minutes || "Duration of session in minutes"}
              inputProps={{ min: 5, max: 480 }}
            />

            {/* 10. Capacity */}
            <TextField
              label="Capacity (Maximum Attendees)"
              type="number"
              fullWidth
              required
              value={formData.capacity}
              onChange={handleChange("capacity")}
              error={Boolean(errors.capacity)}
              helperText={errors.capacity || "Maximum number of registrations allowed"}
              inputProps={{ min: 1, max: 5000 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GroupIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 11. Recurring Interval */}
            <TextField
              select
              label="Recurring Interval"
              fullWidth
              value={formData.recur_interval}
              onChange={handleChange("recur_interval")}
              helperText="Schedule recurrence (None for single session)"
            >
              {RECUR_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === "None" ? "None (One-time session)" : opt}
                </MenuItem>
              ))}
            </TextField>

            {/* 12. Stream / Meeting Link */}
            <TextField
              label="Stream / Meeting Link URL"
              placeholder="https://meet.google.com/abc-defg-hij"
              fullWidth
              value={formData.stream_link}
              onChange={handleChange("stream_link")}
              helperText="Live meeting room URL unlocked 1 hour before session start"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ color: "#64748B" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* 13. Cover Image URL */}
            <TextField
              label="Cover Image URL"
              placeholder="https://images.unsplash.com/..."
              fullWidth
              value={formData.cover_image}
              onChange={handleChange("cover_image")}
              helperText="Optional cover banner image URL displayed on session cards"
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: "#64748B",
              borderColor: "#CBD5E1",
            }}
          >
            Cancel
          </Button>

          {isEdit ? (
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, false)}
                disabled={loading}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#154C3A",
                  borderColor: "#154C3A",
                  "&:hover": { bgcolor: "#ECFDF5", borderColor: "#0D3327" },
                }}
              >
                {loading ? "Saving..." : "Update Only (Skip Email)"}
              </Button>
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading}
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <MailOutlineIcon fontSize="small" />
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#FFFFFF !important",
                  background: "linear-gradient(180deg, #0C8F63 0%, #0A7253 100%)",
                  boxShadow: "0 4px 12px rgba(10, 114, 83, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #06442F 0%, #085A42 100%)",
                    boxShadow: "0 6px 16px rgba(10, 114, 83, 0.35)",
                  },
                }}
              >
                {loading ? "Updating & Notifying..." : "Update & Send Email to Registered Users"}
              </Button>
            </Box>
          ) : (
            <Button
              type="submit"
              disabled={loading}
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} sx={{ color: "#FFFFFF !important" }} /> : null}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "#FFFFFF !important",
                background: "linear-gradient(180deg, #0C8F63 0%, #0A7253 100%)",
                boxShadow: "0 4px 12px rgba(10, 114, 83, 0.25)",
                "&:hover": {
                  background: "linear-gradient(180deg, #06442F 0%, #085A42 100%)",
                  boxShadow: "0 6px 16px rgba(10, 114, 83, 0.35)",
                },
              }}
            >
              {loading ? "Creating..." : "Create Live Event"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
